import * as React from "react";

/**
 * 시즌 스킨 (JCG-3694).
 *
 * - **배포 스위치**: `ACTIVE_SEASON_SKIN` 을 바꿔 배포해 켜고 끈다(기간 자동 종료 없음). `null` 이면 기본 스킨이고
 *   시즌 요소는 렌더되지 않아 시즌 이미지도 내려받지 않는다.
 * - **사용자 탈출구**: 스위치가 켜져 있어도 사용자가 끌 수 있다. 선호는 localStorage `season-skin`("off")에 두고,
 *   루트 layout 의 페인트 전 스크립트(`SEASON_SKIN_SCRIPT`)가 `<html data-season-skin>` 을 붙일지 정한다.
 *   시즌 요소는 모두 `season:` variant(`app/globals.css`)로 이 속성에 묶여 있어 속성 하나로 팝인 없이 켜고 꺼진다.
 *   사용자가 누르는 설정 UI 는 JCG-3696 에서 `useSeasonSkinActive` 로 읽고 `setSeasonSkinEnabled` 로 쓴다.
 * - **시즌 추가**: `SEASONS` 에 항목을 더하고 시즌 전용 CSS 를 `[data-season-skin="<시즌>"]` 로 쓴다.
 */
export type SeasonSkin = "chuseok";

export const ACTIVE_SEASON_SKIN: SeasonSkin | null = "chuseok";

const STORAGE_KEY = "season-skin";
const ROOT_ATTR = "data-season-skin";

/** 시즌 이미지 경로. 대체 텍스트까지 달라지면 `{ src, alt }` 로 적는다 */
type SeasonImageEntry = string | { src: string; alt: string };

interface SeasonConfig {
  /**
   * 기본 이미지 경로 → 시즌 이미지. `SeasonalImage` 가 이 표로 바꿔 끼운다.
   * 점수 감자처럼 경로를 계산해 쓰는 곳도 기본 경로만 넘기면 된다.
   */
  images: Record<string, SeasonImageEntry>;
  favicon: string;
  /** 정답 토스트 문구 뒤에 붙는 장식 */
  correctSuffix: string;
}

const SEASONS: Record<SeasonSkin, SeasonConfig> = {
  chuseok: {
    images: {
      "/logo.webp": "/logo_chuseok.webp",
      "/main/3d_no-bg_butler_half-clothes_gamja.webp": {
        src: "/main/3d_no-bg_chuseok_lantern_gamja.webp",
        alt: "청사초롱을 든 한복 감자",
      },
      "/main/happy_gamza.webp": "/main/happy_gamza_chuseok.webp",
      "/exams/3d_sprout_gamja.webp": "/exams/chuseok/3d_sprout_gamja.webp",
      "/exams/3d_dirt_gamja.webp": "/exams/chuseok/3d_dirt_gamja.webp",
      "/exams/3d_gamja.webp": "/exams/chuseok/3d_gamja.webp",
      "/exams/3d_raining-happy_gamja.webp":
        "/exams/chuseok/3d_raining-happy_gamja.webp",
      "/exams/3d_beach-sprout_gamja.webp":
        "/exams/chuseok/3d_beach-sprout_gamja.webp",
    },
    favicon: "/favicon_chuseok.png",
    correctSuffix: "🌕",
  },
};

const activeSeason = ACTIVE_SEASON_SKIN ? SEASONS[ACTIVE_SEASON_SKIN] : null;

export function getSeasonImage(
  src: string,
): { src: string; alt?: string } | undefined {
  const entry = activeSeason?.images[src];
  if (!entry) return undefined;
  return typeof entry === "string" ? { src: entry } : entry;
}

export const SEASON_FAVICON_SRC = activeSeason?.favicon ?? null;

export const SEASON_CORRECT_SUFFIX = activeSeason?.correctSuffix ?? null;

/**
 * `<html>` 에 시즌 속성을 붙이는 페인트 전 스크립트. 스위치가 꺼져 있으면 `null`.
 * next-themes 테마 스크립트와 같은 방식이며, `<html suppressHydrationWarning>` 이라 hydration 경고가 없다.
 * localStorage 를 못 읽는 환경(사생활 보호 모드 등)에서는 스킨을 켠다.
 */
export const SEASON_SKIN_SCRIPT = ACTIVE_SEASON_SKIN
  ? `(function(){try{if(localStorage.getItem("${STORAGE_KEY}")==="off")return}catch(e){}document.documentElement.setAttribute("${ROOT_ATTR}","${ACTIVE_SEASON_SKIN}")})()`
  : null;

/** 시즌 스킨 켜기/끄기. 선호를 저장하고 새로고침 없이 즉시 반영한다. 클라이언트 전용 */
export function setSeasonSkinEnabled(enabled: boolean): void {
  try {
    if (enabled) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, "off");
  } catch {
    // 저장이 막힌 환경에서도 현재 화면에는 반영한다
  }
  if (!ACTIVE_SEASON_SKIN) return;
  const root = document.documentElement;
  if (enabled) root.setAttribute(ROOT_ATTR, ACTIVE_SEASON_SKIN);
  else root.removeAttribute(ROOT_ATTR);
}

function subscribeSeasonSkin(callback: () => void) {
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [ROOT_ATTR],
  });
  return () => observer.disconnect();
}

/**
 * 지금 화면에 시즌 스킨이 적용돼 있는지(스위치 켜짐 + 사용자가 끄지 않음).
 * `<html>` 속성을 구독하므로 설정에서 토글하면 바로 갱신된다. 서버·hydration 스냅샷은 false.
 */
export function useSeasonSkinActive(): boolean {
  return React.useSyncExternalStore(
    subscribeSeasonSkin,
    () => document.documentElement.hasAttribute(ROOT_ATTR),
    () => false,
  );
}
